import React from 'react';
import { osc, wobble, loop, clamp, easeOut } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES (Anivaze v2) — life-cycle concepts acted out
 * in front of a 5-year-old. Kindergarten-friendly, pure function of
 * `t` (deterministic + exportable). See design-plan/v2/ANIMATION-PATTERN.md.
 */

/* ━━━ Helpers ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

/** Convert polar to cartesian coordinates. */
const polar = (cx: number, cy: number, r: number, deg: number): [number, number] => {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

/** Cross-fade opacity: 0→1 during [riseStart, riseEnd], hold, 1→0 during [fallStart, fallEnd]. */
const xfade = (phase: number, riseStart: number, riseEnd: number, fallStart: number, fallEnd: number): number => {
  if (phase < riseStart) return 0;
  if (phase < riseEnd) return (phase - riseStart) / (riseEnd - riseStart);
  if (phase < fallStart) return 1;
  if (phase < fallEnd) return 1 - (phase - fallStart) / (fallEnd - fallStart);
  return 0;
};

/* ━━━ PlantGrowScene ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * Loop ~8s: seed in soil → tiny sprout → rising stem with leaves → bloom.
 * The sun shines and water drops fall.  Roots grow downward into the soil.
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface PlantGrowSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since the scene started (pure motion driver). */
  t?: number;
}

export const PlantGrowScene: React.FC<PlantGrowSceneProps> = ({
  opacity = 1, accentColor = '#22c55e', t = 0,
  label = 'A tiny seed grows into a flower!',
}) => {
  const PERIOD = 8;             // loop every 8 s
  const ph = loop(t, PERIOD);  // 0→1 phase through the loop

  // Growth phases (as fractions of the loop):
  //   0.00–0.18  seed in soil
  //   0.18–0.40  tiny sprout pokes up
  //   0.40–0.70  stem rises + leaves unfurl
  //   0.70–0.90  flower blooms at the top
  //   0.90–1.00  brief hold then fade back

  // Stem height 0 → 150 px (grows from soilY upward)
  const soilY = 310;
  const stemH = clamp(easeOut(clamp((ph - 0.18) / 0.52, 0, 1)) * 150, 0, 150);
  const stemTopY = soilY - stemH;

  // Seed bobs gently before sprout
  const seedVis = xfade(ph, 0, 0.08, 0.30, 0.44);
  const seedY = soilY - 10 + wobble(t, 1.2, 2);

  // Sprout tip (little arch) rises from soil before the full stem
  const sproutVis = xfade(ph, 0.16, 0.26, 0.42, 0.52);
  const sproutH = clamp(easeOut(clamp((ph - 0.16) / 0.14, 0, 1)) * 28, 0, 28);

  // Leaves unfurl (left and right) when stem is > 40 % grown
  const leafProg = clamp(easeOut(clamp((ph - 0.50) / 0.20, 0, 1)), 0, 1);
  const leafVis = clamp((ph - 0.48) / 0.08, 0, 1);

  // Flower bloom
  const flowerProg = clamp(easeOut(clamp((ph - 0.68) / 0.18, 0, 1)), 0, 1);
  const flowerVis = clamp((ph - 0.68) / 0.10, 0, 1);
  const petalR = flowerProg * 18;

  // Roots grow downward
  const rootDepth = clamp(easeOut(clamp((ph - 0.12) / 0.30, 0, 1)) * 50, 0, 50);

  // Sun rays slowly rotate
  const sunAng = (t * 4) % 360;

  // Watering-can rain drops: 4 drops at staggered phases
  const dropBasePhase = loop(t, 1.4);   // one set of drops per 1.4 s
  const drops = Array.from({ length: 4 }, (_, i) => {
    const dp = (dropBasePhase + i * 0.25) % 1;
    const dropX = 570 + i * 12;
    const dropY = 120 + dp * 160;
    const dropOp = dp < 0.85 ? 1 : 1 - (dp - 0.85) / 0.15;
    return { x: dropX, y: dropY, op: dropOp };
  });

  // Ground shimmer
  const grassWave = wobble(t, 1.8, 3);

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="pg-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="100%" stopColor="#e0f2fe" />
        </linearGradient>
        <radialGradient id="pg-sun" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fffde4" />
          <stop offset="60%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
        <linearGradient id="pg-soil" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#92400e" />
          <stop offset="100%" stopColor="#713f12" />
        </linearGradient>
      </defs>

      {/* Sky */}
      <rect x={0} y={0} width={800} height={450} fill="url(#pg-sky)" />

      {/* Soil block */}
      <rect x={0} y={soilY} width={800} height={450 - soilY} fill="url(#pg-soil)" />
      {/* Grass fringe */}
      {Array.from({ length: 20 }, (_, i) => {
        const gx = i * 42 + 10;
        const gh = 12 + (i % 3) * 6;
        const gw = grassWave * ((i % 2 === 0) ? 1 : -1);
        return (
          <path key={i}
            d={`M${gx},${soilY} Q${gx + gw + 6},${soilY - gh * 0.6} ${gx + 6},${soilY - gh}`}
            fill="none" stroke="#4ade80" strokeWidth={3} strokeLinecap="round" />
        );
      })}

      {/* Sun (top-left) */}
      <g transform="translate(90 80)">
        <g transform={`rotate(${sunAng})`} opacity={0.85}>
          {Array.from({ length: 12 }, (_, k) => {
            const a = (k / 12) * Math.PI * 2;
            return (
              <line key={k}
                x1={38 * Math.cos(a)} y1={38 * Math.sin(a)}
                x2={54 * Math.cos(a)} y2={54 * Math.sin(a)}
                stroke="#fcd34d" strokeWidth={3.5} strokeLinecap="round" />
            );
          })}
        </g>
        <circle r={30} fill="url(#pg-sun)" />
        {/* Smiley */}
        <circle cx={-8} cy={-4} r={3.5} fill="#92400e" />
        <circle cx={8} cy={-4} r={3.5} fill="#92400e" />
        <path d="M-9,8 Q0,18 9,8" fill="none" stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
      </g>

      {/* Drifting cloud */}
      {[{ baseX: 620, y: 72, s: 1 }, { baseX: 320, y: 108, s: 0.75 }].map((c, i) => {
        const cx = ((c.baseX - (t * (14 + i * 6)) % 700) + 700) % 700;
        return (
          <g key={i} transform={`translate(${cx} ${c.y}) scale(${c.s})`} opacity={0.9}>
            <ellipse cx={0} cy={0} rx={36} ry={22} fill="#fff" />
            <ellipse cx={36} cy={6} rx={28} ry={18} fill="#fff" />
            <ellipse cx={-28} cy={8} rx={24} ry={15} fill="#fff" />
          </g>
        );
      })}

      {/* Watering can */}
      <g transform="translate(540 62)" opacity={clamp((ph - 0.12) / 0.10, 0, 1)}>
        {/* body */}
        <rect x={0} y={0} width={50} height={34} rx={8} fill="#60a5fa" stroke="#3b82f6" strokeWidth={2} />
        {/* spout */}
        <path d="M50,22 L80,34 L78,40 L46,30 Z" fill="#60a5fa" stroke="#3b82f6" strokeWidth={1.5} />
        {/* handle */}
        <path d="M0,4 Q-18,0 -18,17 Q-18,34 0,30" fill="none" stroke="#3b82f6" strokeWidth={4} strokeLinecap="round" />
      </g>

      {/* Rain drops */}
      {drops.map((d, i) => (
        <ellipse key={i} cx={d.x} cy={d.y} rx={3} ry={5} fill="#93c5fd" opacity={d.op}
          transform={`rotate(15 ${d.x} ${d.y})`} />
      ))}

      {/* Plant centre X */}
      <g transform="translate(400 0)">
        {/* Roots grow down */}
        <g opacity={clamp((ph - 0.14) / 0.12, 0, 1)}>
          {[{ dx: -12, bend: -8 }, { dx: 0, bend: 4 }, { dx: 14, bend: -5 }].map((r, i) => (
            <path key={i}
              d={`M${r.dx},${soilY + 4} Q${r.dx + r.bend},${soilY + rootDepth * 0.5} ${r.dx + r.bend * 1.4},${soilY + rootDepth}`}
              fill="none" stroke="#a16207" strokeWidth={3} strokeLinecap="round" opacity={0.75} />
          ))}
        </g>

        {/* Seed */}
        <g opacity={seedVis}>
          <ellipse cx={0} cy={seedY} rx={12} ry={9} fill="#a16207" stroke="#713f12" strokeWidth={2} />
          <line x1={0} y1={seedY - 9} x2={0} y2={seedY - 15} stroke="#4ade80" strokeWidth={2.5} strokeLinecap="round" />
        </g>

        {/* Tiny sprout arch */}
        <g opacity={sproutVis}>
          <path
            d={`M0,${soilY} Q12,${soilY - sproutH * 0.8} 0,${soilY - sproutH}`}
            fill="none" stroke="#4ade80" strokeWidth={4} strokeLinecap="round" />
          <circle cx={0} cy={soilY - sproutH} r={5} fill="#86efac" />
        </g>

        {/* Stem */}
        {stemH > 2 && (
          <line
            x1={0} y1={soilY}
            x2={wobble(t, 2.2, 4)} y2={stemTopY}
            stroke="#16a34a" strokeWidth={6} strokeLinecap="round" />
        )}

        {/* Left leaf */}
        <g opacity={leafVis} transform={`translate(${wobble(t, 2.2, 4)} ${stemTopY + stemH * 0.45})`}>
          <path
            d={`M0,0 Q${-leafProg * 40},${-leafProg * 18} ${-leafProg * 58},${leafProg * 4}`}
            fill="none" stroke="#22c55e" strokeWidth={5} strokeLinecap="round" />
          <ellipse cx={-leafProg * 32} cy={-leafProg * 10} rx={leafProg * 22} ry={leafProg * 11}
            fill="#4ade80" opacity={0.7} transform={`rotate(-20 ${-leafProg * 32} ${-leafProg * 10})`} />
        </g>

        {/* Right leaf */}
        <g opacity={leafVis} transform={`translate(${wobble(t, 2.2, 4)} ${stemTopY + stemH * 0.62})`}>
          <path
            d={`M0,0 Q${leafProg * 42},${-leafProg * 16} ${leafProg * 56},${leafProg * 6}`}
            fill="none" stroke="#22c55e" strokeWidth={5} strokeLinecap="round" />
          <ellipse cx={leafProg * 30} cy={-leafProg * 8} rx={leafProg * 22} ry={leafProg * 10}
            fill="#4ade80" opacity={0.7} transform={`rotate(18 ${leafProg * 30} ${-leafProg * 8})`} />
        </g>

        {/* Flower at top */}
        <g opacity={flowerVis} transform={`translate(${wobble(t, 2.2, 4)} ${stemTopY})`}>
          {/* Petals */}
          {Array.from({ length: 6 }, (_, k) => {
            const [px, py] = polar(0, 0, petalR * 1.4, k * 60 + (t * 12) % 360);
            return (
              <ellipse key={k} cx={px} cy={py} rx={petalR * 0.7} ry={petalR * 0.5}
                fill={accentColor} opacity={0.88}
                transform={`rotate(${k * 60} ${px} ${py})`} />
            );
          })}
          {/* Centre */}
          <circle r={petalR * 0.6} fill="#fde68a" stroke="#f59e0b" strokeWidth={2} />
          {/* Dots */}
          {flowerProg > 0.6 && Array.from({ length: 5 }, (_, k) => {
            const [dx, dy] = polar(0, 0, petalR * 0.3, k * 72);
            return <circle key={k} cx={dx} cy={dy} r={2} fill="#92400e" />;
          })}
        </g>
      </g>

      {/* Caption */}
      <g transform="translate(400 428)">
        <rect x={-240} y={-20} width={480} height={36} rx={18} fill="rgba(255,255,255,0.88)" stroke="#bbf7d0" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#14532d" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ FrogLifeScene ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * Loop ~9s: frogspawn (jelly eggs) → tadpole (wiggling tail) → tadpole
 * grows legs → froglet → full frog on a lily pad that hops.  Each stage
 * cross-fades with a small label.
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface FrogLifeSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since the scene started (pure motion driver). */
  t?: number;
}

export const FrogLifeScene: React.FC<FrogLifeSceneProps> = ({
  opacity = 1, accentColor = '#4ade80', t = 0,
  label = 'From egg to frog!',
}) => {
  const PERIOD = 9;
  const ph = loop(t, PERIOD);   // 0→1 through the whole cycle

  // Stage boundaries (loop phase fractions):
  //   0.00–0.20  frogspawn cluster
  //   0.18–0.40  tadpole (just head + tail)
  //   0.38–0.60  tadpole with back legs
  //   0.58–0.78  froglet (front legs too, tiny tail stub)
  //   0.76–1.00  full frog on lily pad + hops

  const spawnOp   = xfade(ph, 0.00, 0.08, 0.18, 0.26);
  const tadpOp    = xfade(ph, 0.18, 0.26, 0.38, 0.46);
  const legTadOp  = xfade(ph, 0.38, 0.46, 0.58, 0.66);
  const frogletOp = xfade(ph, 0.58, 0.66, 0.76, 0.84);
  const frogOp    = xfade(ph, 0.76, 0.84, 0.96, 1.00);

  // Shared pond geometry
  const pondCX = 400, pondCY = 300, pondRX = 340, pondRY = 100;
  const waterRipple = osc(t, 2.6, 0.85, 1.0);    // gentle ripple on pond opacity

  // Spawn: cluster of 7 jelly eggs with dark dots
  const spawnEggs = Array.from({ length: 7 }, (_, i) => {
    // pseudo-random but deterministic positions derived from index
    const ex = pondCX - 80 + (i * 31 % 160);
    const ey = pondCY - 20 + (i * 17 % 40) + wobble(t, 2.0 + i * 0.3, 3, i);
    return { x: ex, y: ey };
  });

  // Tadpole motion: swims left-right across pond with wiggling tail
  const tadX = pondCX - 120 + Math.sin((t / 3.2) * Math.PI * 2) * 100;
  const tadY = pondCY - 10 + wobble(t, 1.8, 10);
  const tailWag = wobble(t, 0.28, 18);       // fast tail wag
  const tailMid = wobble(t, 0.28, 10, 1.1);

  // Frog hop (stage 5): a vertical bounce driven by phase within the full cycle
  const hopCycle = loop(t, 2.2);              // one hop every 2.2 s
  const hopY = clamp(easeOut(Math.abs(Math.sin(hopCycle * Math.PI * 2))) * -44, -44, 0);
  const frogY = pondCY - 68 + hopY;
  const frogLegSpread = 6 + clamp(-hopY * 0.4, 0, 12);  // legs splay wider mid-hop
  const frogBodyScale = hopY < -8 ? 1 - clamp(-hopY / 44, 0, 1) * 0.12 : 1; // squish on takeoff

  // Lily pad sways
  const lilyWobble = wobble(t, 3.4, 4);

  // Stage label text
  const stageLabel =
    frogOp > 0.5   ? 'Frog!' :
    frogletOp > 0.5 ? 'Froglet' :
    legTadOp > 0.5  ? 'Growing legs…' :
    tadpOp > 0.5    ? 'Tadpole' :
                      'Frogspawn';

  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="fl-pond" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#bae6fd" />
          <stop offset="100%" stopColor="#38bdf8" />
        </radialGradient>
        <radialGradient id="fl-sky" cx="50%" cy="30%">
          <stop offset="0%" stopColor="#e0f2fe" />
          <stop offset="100%" stopColor="#7dd3fc" />
        </radialGradient>
        <radialGradient id="fl-sun" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fffde4" />
          <stop offset="70%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
      </defs>

      {/* Sky */}
      <rect x={0} y={0} width={800} height={450} fill="url(#fl-sky)" />

      {/* Sun */}
      <g transform="translate(96 72)">
        <g transform={`rotate(${(t * 5) % 360})`} opacity={0.8}>
          {Array.from({ length: 10 }, (_, k) => {
            const a = (k / 10) * Math.PI * 2;
            return (
              <line key={k}
                x1={34 * Math.cos(a)} y1={34 * Math.sin(a)}
                x2={48 * Math.cos(a)} y2={48 * Math.sin(a)}
                stroke="#fcd34d" strokeWidth={3} strokeLinecap="round" />
            );
          })}
        </g>
        <circle r={26} fill="url(#fl-sun)" />
      </g>

      {/* Cloud */}
      {[{ bx: 580, y: 68, s: 1 }, { bx: 260, y: 110, s: 0.72 }].map((c, i) => {
        const cx = ((c.bx - (t * (12 + i * 5)) % 680) + 680) % 680;
        return (
          <g key={i} transform={`translate(${cx} ${c.y}) scale(${c.s})`} opacity={0.9}>
            <ellipse cx={0} cy={0} rx={38} ry={22} fill="#fff" />
            <ellipse cx={38} cy={8} rx={28} ry={18} fill="#fff" />
            <ellipse cx={-30} cy={9} rx={22} ry={15} fill="#fff" />
          </g>
        );
      })}

      {/* Grass bank */}
      <ellipse cx={400} cy={420} rx={420} ry={80} fill="#4ade80" />
      {Array.from({ length: 14 }, (_, i) => {
        const gx = 60 + i * 52;
        const gh = 10 + (i % 3) * 5;
        const gw = wobble(t, 2.0 + i * 0.1, 2.5, i);
        return (
          <path key={i}
            d={`M${gx},380 Q${gx + gw + 5},${380 - gh * 0.6} ${gx + 5},${380 - gh}`}
            fill="none" stroke="#22c55e" strokeWidth={2.5} strokeLinecap="round" />
        );
      })}

      {/* Pond */}
      <ellipse cx={pondCX} cy={pondCY} rx={pondRX} ry={pondRY} fill="url(#fl-pond)" opacity={waterRipple} />
      {/* Ripple rings */}
      {[0.7, 0.85, 1.0].map((rs, i) => (
        <ellipse key={i} cx={pondCX} cy={pondCY}
          rx={pondRX * rs} ry={pondRY * rs}
          fill="none" stroke="#93c5fd"
          strokeWidth={1.5 - i * 0.4}
          opacity={osc(t, 2.2 + i * 0.4, 0.1, 0.5, i * 1.1)} />
      ))}

      {/* Lily pad */}
      <g transform={`translate(${pondCX + 60 + lilyWobble} ${pondCY + 28})`}>
        <ellipse cx={0} cy={0} rx={52} ry={28} fill="#16a34a" opacity={0.9} />
        <ellipse cx={0} cy={0} rx={52} ry={28} fill="none" stroke="#15803d" strokeWidth={2} />
        {/* notch */}
        <path d="M0,-28 L0,0" fill="none" stroke="#15803d" strokeWidth={2} />
        <path d="M0,-28 L14,0" fill="none" stroke="#15803d" strokeWidth={1.5} opacity={0.6} />
      </g>

      {/* ── Stage 1: Frogspawn ─────────────────────────────────────────────── */}
      <g opacity={spawnOp}>
        {spawnEggs.map((e, i) => (
          <g key={i} transform={`translate(${e.x} ${e.y})`}>
            <circle r={10} fill="rgba(200,240,255,0.55)" stroke="#93c5fd" strokeWidth={1.2} />
            <circle r={4} fill="#1a1a2e" opacity={0.85} />
          </g>
        ))}
        {/* Jelly strand connecting them */}
        <path
          d={`M${spawnEggs[0].x},${spawnEggs[0].y} ${spawnEggs.slice(1).map(e => `L${e.x},${e.y}`).join(' ')}`}
          fill="none" stroke="rgba(180,230,255,0.5)" strokeWidth={8} strokeLinejoin="round" />
      </g>

      {/* ── Stage 2: Plain tadpole ─────────────────────────────────────────── */}
      <g opacity={tadpOp} transform={`translate(${tadX} ${tadY})`}>
        {/* Tail: cubic bezier that wags */}
        <path
          d={`M-4,0 Q${-24 + tailMid},${10} ${-52 + tailWag},${-2}`}
          fill="none" stroke="#64748b" strokeWidth={6} strokeLinecap="round" />
        {/* Body */}
        <ellipse cx={6} cy={0} rx={18} ry={12} fill="#475569" />
        {/* Eye */}
        <circle cx={16} cy={-4} r={4} fill="#1e293b" />
        <circle cx={17} cy={-5} r={1.5} fill="#fff" />
      </g>

      {/* ── Stage 3: Tadpole with back legs ───────────────────────────────── */}
      <g opacity={legTadOp} transform={`translate(${tadX} ${tadY})`}>
        <path
          d={`M-4,0 Q${-24 + tailMid * 0.7},${10} ${-50 + tailWag * 0.7},${-2}`}
          fill="none" stroke="#64748b" strokeWidth={5} strokeLinecap="round" />
        <ellipse cx={6} cy={0} rx={18} ry={12} fill="#475569" />
        <circle cx={16} cy={-4} r={4} fill="#1e293b" />
        <circle cx={17} cy={-5} r={1.5} fill="#fff" />
        {/* Back legs */}
        <path d={`M-4,8 Q${-6 + wobble(t, 0.5, 6, 0)},${18} ${-2},${24}`}
          fill="none" stroke="#4ade80" strokeWidth={4} strokeLinecap="round" />
        <path d={`M4,9 Q${6 + wobble(t, 0.5, 6, 1.6)},${19} ${10},${25}`}
          fill="none" stroke="#4ade80" strokeWidth={4} strokeLinecap="round" />
      </g>

      {/* ── Stage 4: Froglet ──────────────────────────────────────────────── */}
      <g opacity={frogletOp} transform={`translate(${tadX} ${tadY - 10})`}>
        {/* Tiny tail stub */}
        <path d="M-8,4 Q-20,8 -22,2" fill="none" stroke="#64748b" strokeWidth={3} strokeLinecap="round" />
        {/* Body */}
        <ellipse cx={4} cy={0} rx={20} ry={14} fill={accentColor} />
        {/* Eyes */}
        <circle cx={14} cy={-10} r={6} fill="#ecfccb" stroke="#4ade80" strokeWidth={1.5} />
        <circle cx={14} cy={-10} r={3} fill="#1a1a2e" />
        {/* Back legs */}
        <path d={`M-8,8 Q${-14 + wobble(t, 0.55, 7)},${20} ${-6},${28}`}
          fill="none" stroke="#16a34a" strokeWidth={5} strokeLinecap="round" />
        <path d={`M6,9 Q${12 + wobble(t, 0.55, 7, 1.6)},${21} ${14},${29}`}
          fill="none" stroke="#16a34a" strokeWidth={5} strokeLinecap="round" />
        {/* Front legs */}
        <path d="M8,-6 Q18,-14 20,-8" fill="none" stroke="#16a34a" strokeWidth={4} strokeLinecap="round" />
        <path d="M-4,-6 Q-14,-14 -16,-8" fill="none" stroke="#16a34a" strokeWidth={4} strokeLinecap="round" />
      </g>

      {/* ── Stage 5: Full frog hopping on lily pad ────────────────────────── */}
      <g opacity={frogOp} transform={`translate(${pondCX + 60 + lilyWobble} ${frogY})`}>
        <g transform={`scale(1 ${frogBodyScale})`}>
          {/* Back legs splay on hop */}
          <path
            d={`M-16,12 Q${-32 - frogLegSpread},${26} ${-24 - frogLegSpread * 2},${34}`}
            fill="none" stroke="#15803d" strokeWidth={7} strokeLinecap="round" />
          <path
            d={`M16,12 Q${32 + frogLegSpread},${26} ${24 + frogLegSpread * 2},${34}`}
            fill="none" stroke="#15803d" strokeWidth={7} strokeLinecap="round" />
          {/* Body */}
          <ellipse cx={0} cy={0} rx={28} ry={20} fill={accentColor} />
          {/* Belly */}
          <ellipse cx={0} cy={6} rx={18} ry={12} fill="#bbf7d0" opacity={0.7} />
          {/* Front legs */}
          <path d="M18,-8 Q28,-18 30,-10" fill="none" stroke="#16a34a" strokeWidth={5} strokeLinecap="round" />
          <path d="M-18,-8 Q-28,-18 -30,-10" fill="none" stroke="#16a34a" strokeWidth={5} strokeLinecap="round" />
          {/* Bulgy eyes */}
          <circle cx={-14} cy={-18} r={9} fill="#ecfccb" stroke="#4ade80" strokeWidth={2} />
          <circle cx={14} cy={-18} r={9} fill="#ecfccb" stroke="#4ade80" strokeWidth={2} />
          <circle cx={-14} cy={-18} r={4.5} fill="#1a1a2e" />
          <circle cx={14} cy={-18} r={4.5} fill="#1a1a2e" />
          {/* Glints */}
          <circle cx={-12} cy={-20} r={1.8} fill="#fff" />
          <circle cx={16} cy={-20} r={1.8} fill="#fff" />
          {/* Mouth */}
          <path d="M-10,8 Q0,14 10,8" fill="none" stroke="#15803d" strokeWidth={2.5} strokeLinecap="round" />
        </g>
        {/* Shadow under frog that shrinks during hop */}
        <ellipse cx={0} cy={28 - hopY * 0.35} rx={24 + hopY * 0.4} ry={5 + hopY * 0.2}
          fill="rgba(0,0,0,0.18)" />
      </g>

      {/* Stage label badge */}
      <g transform={`translate(400 204)`}>
        <rect x={-72} y={-16} width={144} height={30} rx={15}
          fill="rgba(255,255,255,0.82)" stroke="#bbf7d0" strokeWidth={1.5} />
        <text x={0} y={4} textAnchor="middle" fill="#14532d" fontSize={13} fontWeight={700} fontFamily="Inter,sans-serif">
          {stageLabel}
        </text>
      </g>

      {/* Caption */}
      <g transform="translate(400 428)">
        <rect x={-200} y={-20} width={400} height={36} rx={18} fill="rgba(255,255,255,0.88)" stroke="#bbf7d0" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#14532d" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
